/**
 * Turntable — pure derivations for the 360° viewer.
 *
 * Ports the arithmetic of the demo's `Turntable` class and `turnStill()`
 * (`OEM+_Platform_Demo.html` lines 1762–1831): frame ↔ angle, the eight
 * snapshot angles, which components earn a callout, and where each callout
 * label sits. No React, no DOM, no clock — the viewer in `Turntable.tsx`
 * renders what these return.
 *
 * Transformation layer.
 *
 * @see src/versions/v1/shared/viz/Turntable.tsx
 * @see docs/logic/fleet/machine-page.md
 */

import type { ComponentHealth } from '../../data/core';
import {
  ATTENTION_BANDS,
  CONDITION_BAND_LABEL,
  CONDITION_BAND_ORDER,
  type ConditionBand,
} from '../../schema/machine';
import { healthBand } from '../oem-engine';

/** One frame set — the turntable itself, or its exploded variant. */
export interface FrameSet {
  /** Frame count: 24 for the turntable, 6 exploded. */
  n: number;
  /** Angle of frame 0, degrees. */
  start: number;
  /** Per-frame callout anchors, component → `[x, y]` as fractions of w/h. */
  anchors: ReadonlyArray<Readonly<Record<string, readonly [number, number]>>>;
}

/**
 * Demo line 1762. `TSNAPS` — the eight named angles in the snapshot strip.
 * 270° is the side elevation the viewer opens on.
 */
export const TURN_SNAPS: ReadonlyArray<readonly [number, string]> = [
  [270, 'Side'],
  [315, 'Front ¾'],
  [0, 'Front'],
  [45, 'Front ¾ R'],
  [90, 'Side R'],
  [135, 'Rear ¾ R'],
  [180, 'Rear'],
  [225, 'Rear ¾'],
];

/** The angle the viewer opens on — the side elevation. Demo line 1766. */
export const TURN_HOME_DEG = 270;

/** Demo line 1768. `idx(deg)` — the frame nearest an angle. */
export function frameIndex(deg: number, set: Pick<FrameSet, 'n' | 'start'>): number {
  const raw = Math.round(((deg - set.start + 360) % 360) / (360 / set.n));
  return ((raw % set.n) + set.n) % set.n;
}

/** Demo line 1769. `angle()` — the angle a frame shows. */
export function frameAngle(i: number, set: Pick<FrameSet, 'n' | 'start'>): number {
  return (set.start + (i * 360) / set.n) % 360;
}

/** Step `k` frames round the set, wrapping. Demo line 1787. */
export function stepFrame(i: number, k: number, n: number): number {
  return (((i + k) % n) + n) % n;
}

/** Demo line 1789. The snapshot whose angle is nearest the current one. */
export function nearestSnap(angle: number): number {
  let best = TURN_SNAPS[0][0];
  let bestDiff = Infinity;
  for (const [deg] of TURN_SNAPS) {
    const diff = Math.min(Math.abs(deg - angle), 360 - Math.abs(deg - angle));
    if (diff < bestDiff) {
      best = deg;
      bestDiff = diff;
    }
  }
  return best;
}

/**
 * Demo line 1770. `flagged()` — the rows in an attention band, worst band
 * first, then lowest score.
 */
export function flaggedComponents(health: readonly ComponentHealth[]): ComponentHealth[] {
  return health
    .filter((h) => ATTENTION_BANDS.includes(healthBand(h)))
    .sort(
      (a, b) =>
        CONDITION_BAND_ORDER[healthBand(a)] - CONDITION_BAND_ORDER[healthBand(b)] ||
        a.health_score - b.health_score,
    );
}

/**
 * Every register row, worst band first, then lowest score — the order of the
 * demo's component list beside the viewer (`openMachine`, line 2177).
 */
export function componentsBySeverity(health: readonly ComponentHealth[]): ComponentHealth[] {
  return [...health].sort(
    (a, b) =>
      CONDITION_BAND_ORDER[healthBand(a)] - CONDITION_BAND_ORDER[healthBand(b)] ||
      a.health_score - b.health_score,
  );
}

/** One callout, resolved to image pixels. */
export interface TurnCallout {
  component: string;
  band: ConditionBand;
  /** `Component · Band · 62/100`. */
  label: string;
  /** Anchor point on the machine. */
  x: number;
  y: number;
  /** Label box, top-left. */
  lx: number;
  ly: number;
  /** Label box width. Height is fixed at {@link CALLOUT_H}. */
  lw: number;
}

/** Label box height, image px. Demo line 1817. */
export const CALLOUT_H = 24;

/** A dragged label's offset from its anchor, image px. Demo `TURNPOS`. */
export interface CalloutOffset {
  dx: number;
  dy: number;
}

const calloutLabel = (h: ComponentHealth, band: ConditionBand): string =>
  `${h.component} · ${CONDITION_BAND_LABEL[band]} · ${h.health_score}/100`;

/**
 * Demo lines 1809–1813. `overlay()` — anchor each flagged component on the
 * current frame, then place its label at a staggered default offset, nudging
 * down (and across once it runs out of room) until it clears the labels
 * already placed. A label the reader has dragged keeps its offset and skips
 * the nudge. Anchors more than 20 px outside the frame are dropped.
 */
export function layoutCallouts(
  flagged: readonly ComponentHealth[],
  anchors: Readonly<Record<string, readonly [number, number]>>,
  w: number,
  h: number,
  offsets: Readonly<Record<string, CalloutOffset>> = {},
): TurnCallout[] {
  const placed: Array<{ x: number; y: number; w: number }> = [];
  const out: TurnCallout[] = [];
  flagged.forEach((row, k) => {
    const an = anchors[row.component];
    if (!an) return;
    const x = an[0] * w;
    const y = an[1] * h;
    if (x < -20 || x > w + 20 || y < -20 || y > h + 20) return;
    const band = healthBand(row);
    const label = calloutLabel(row, band);
    const lw = label.length * 6.4 + 18;
    const own = offsets[row.component];
    const off = own ?? { dx: (k % 2 ? 1 : -1) * (70 + k * 12), dy: -(64 + k * 30) };
    let lx = Math.max(4, Math.min(w - lw - 4, x + off.dx - lw / 2));
    let ly = Math.max(4, Math.min(h - 30, y + off.dy));
    if (!own) {
      let guard = 0;
      const clash = () =>
        placed.some(
          (r) => lx < r.x + r.w + 6 && lx + lw + 6 > r.x && ly < r.y + 30 && ly + 30 > r.y,
        );
      while (guard++ < 12 && clash()) {
        ly += 30;
        if (ly > h - 30) {
          ly = 4;
          lx = Math.max(4, Math.min(w - lw - 4, lx + (k % 2 ? -1 : 1) * 120));
        }
      }
    }
    placed.push({ x: lx, y: ly, w: lw });
    out.push({ component: row.component, band, label, x, y, lx, ly, lw });
  });
  return out;
}

/**
 * Demo lines 1826–1829. `turnStill()` — the non-interactive side elevation:
 * callouts for the flagged rows (or just `component`, when given) at the
 * still's simpler fixed offsets.
 */
export function stillCallouts(
  health: readonly ComponentHealth[],
  anchors: Readonly<Record<string, readonly [number, number]>>,
  w: number,
  h: number,
  component: string | null,
): TurnCallout[] {
  const out: TurnCallout[] = [];
  health
    .filter(
      (row) =>
        ATTENTION_BANDS.includes(healthBand(row)) && (!component || row.component === component),
    )
    .forEach((row, k) => {
      const an = anchors[row.component];
      if (!an) return;
      const x = an[0] * w;
      const y = an[1] * h;
      const band = healthBand(row);
      const label = calloutLabel(row, band);
      const lw = label.length * 6.4 + 18;
      const lx = Math.max(4, Math.min(w - lw - 4, x - lw / 2 + (k % 2 ? 70 : -70)));
      const ly = Math.max(4, y - (80 + k * 24));
      out.push({ component: row.component, band, label, x, y, lx, ly, lw });
    });
  return out;
}

/**
 * Demo line 1800. The red-channel value that marks a component's pixels in
 * the ID mask: `(compidx + 1) × 16`. `null` when the model has no mask
 * entry for the component.
 */
export function maskTarget(
  compidx: Readonly<Record<string, number>>,
  component: string | null,
): number | null {
  if (!component || !(component in compidx)) return null;
  return (compidx[component] + 1) * 16;
}
